import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OctagonAlert, Power } from "lucide-react";
import { toast } from "sonner";

export function KillSwitchPanel({ engaged }) {
  const qc = useQueryClient();
  const [pw, setPw] = useState("");
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["paper"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success(msg); setPw(""); };
  const engage = useMutation({
    mutationFn: async () => (await api.post("/paper/kill-switch", { engage: true, reason: "owner emergency stop" })).data,
    onSuccess: (r) => done(`Kill switch engaged · ${r.flattened} position(s) flattened`),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const release = useMutation({
    mutationFn: async () => {
      const isCode = /^\d{6}$/.test(pw);
      const { data } = await api.post("/auth/step-up/risk-control", isCode ? { totp_code: pw } : { password: pw });
      return (await api.post("/paper/kill-switch", { engage: false, step_up_token: data.step_up_token })).data;
    },
    onSuccess: () => done("Kill switch released"),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Panel title="Emergency Kill Switch" testId="kill-switch-panel">
      <div className="p-4 space-y-3">
        <div data-testid="kill-switch-status" className={engaged ? "text-rose-400 text-sm font-semibold flex items-center gap-2" : "text-emerald-400 text-sm font-semibold flex items-center gap-2"}>
          <OctagonAlert className="w-4 h-4" />{engaged ? "ENGAGED — all trading blocked" : "Off — trading allowed within risk limits"}
        </div>
        {!engaged ? (
          <Button data-testid="kill-switch-engage-btn" onClick={() => engage.mutate()} disabled={engage.isPending} className="w-full bg-rose-600 hover:bg-rose-500 text-white">
            <Power className="w-4 h-4 mr-2" />Engage: cancel orders and flatten all
          </Button>
        ) : (
          <div className="flex gap-2">
            <Input data-testid="kill-switch-reverify-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password or 6-digit code" className="bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
            <Button data-testid="kill-switch-release-btn" onClick={() => release.mutate()} disabled={!pw || release.isPending} variant="outline" className="border-[var(--border-subtle)] shrink-0">Release</Button>
          </div>
        )}
        <p className="text-[11px] text-slate-500">Engaging needs no confirmation (fail-safe). Releasing requires re-verification. A drawdown breach engages it automatically.</p>
      </div>
    </Panel>
  );
}
